import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Shell from '../../components/Shell';
import CoverVisual from '../../components/CoverVisual';
import api from '../../services/api';
import { formatKeyTermLabel } from '../../utils/formatKeyTermLabel';
import { pillarLabel } from '../../visuals/portalImagery';

type FundDetail = {
  slug: string;
  name: string;
  pillar: string;
  status: string;
  image?: string;
  keyTerms: Record<string, string>;
  summaryHtml: string;
  documents: { id: string; title: string; kind: string; exists: boolean }[];
};

export default function FundDetailPage() {
  const { slug } = useParams();
  const [fund, setFund] = useState<FundDetail | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/portal/funds/${slug}`)
      .then((r) => setFund(r.data))
      .catch(() => setError('Fund not found.'));
  }, [slug]);

  async function openDoc(id: string) {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`/api/v1/portal/funds/${slug}/documents/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      alert('Document unavailable');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
  }

  return (
    <Shell title="Fund detail">
      <Link to="/portal" className="text-sm text-ink-700 underline">
        ← All funds
      </Link>
      {error && <p className="mt-4 text-red-700">{error}</p>}
      {fund && (
        <div className="mt-6 animate-fade-up">
          <CoverVisual
            pillar={fund.pillar}
            slug={fund.slug}
            image={fund.image}
            alt={fund.name}
            variant="hero"
            className="mb-8"
          />
          <p className="text-xs uppercase tracking-[0.22em] text-navy-700">
            {pillarLabel[fund.pillar] || fund.pillar}
            <span className="mx-2 text-mist-200">·</span>
            {fund.status}
          </p>
          <h1 className="mt-2 font-display text-3xl text-ink-900">
            {fund.name}
          </h1>
          <dl className="mt-6 grid gap-3 sm:grid-cols-2">
            {Object.entries(fund.keyTerms).map(([k, v]) => (
              <div key={k} className="border border-mist-200 bg-white px-4 py-3">
                <dt className="text-xs uppercase tracking-wide text-ink-700">
                  {formatKeyTermLabel(k)}
                </dt>
                <dd className="mt-1 text-sm font-medium text-ink-900">{v}</dd>
              </div>
            ))}
          </dl>
          <div
            className="prose-fund mt-10 max-w-3xl"
            dangerouslySetInnerHTML={{ __html: fund.summaryHtml }}
          />
          {fund.documents.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-xl">Documents</h2>
              <ul className="mt-3 space-y-2">
                {fund.documents.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      disabled={!d.exists}
                      onClick={() => openDoc(d.id)}
                      className="text-sm text-navy-700 underline disabled:text-ink-700 disabled:no-underline"
                    >
                      {d.title}
                      {!d.exists ? ' (missing file)' : ''}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Link
            to="/portal/interest"
            className="mt-10 inline-block bg-navy-900 px-5 py-2.5 text-sm font-semibold text-mist-50 hover:bg-navy-700"
          >
            Express soft interest
          </Link>
        </div>
      )}
    </Shell>
  );
}
